import { Button, Divider, Flex, Space, Tag, Tooltip, Typography } from 'antd'
import { Pencil, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { isOwnerRole } from './constants'
import type { EditableWorkspaceRole } from './types'
import { isWorkspaceRoleReadOnly } from './utils'

const { Text } = Typography

type Props = {
  role: EditableWorkspaceRole
  onEdit: (role: EditableWorkspaceRole) => void
  onDelete: (role: EditableWorkspaceRole) => void
  onSelectAll?: () => void
  onDeselectAll?: () => void
  /** False when the viewer lacks `roles.manage` — hides every write action. */
  canManage?: boolean
}

/**
 * One role column's header in the permissions matrix: the role's identity plus
 * its edit/delete actions and the bulk select/deselect links. Mirrors
 * `RoleColumnHeader` on the apps side.
 */
export const WorkspaceRoleColumnHeader = ({
  role,
  onEdit,
  onDelete,
  onSelectAll,
  onDeselectAll,
  canManage = true
}: Props) => {
  const { t } = useTranslation()
  const isOwner = isOwnerRole(role.codename)
  const isFixed = isWorkspaceRoleReadOnly(role) || !canManage

  return (
    <Flex vertical style={{ width: '100%' }}>
      <Flex justify="space-between" align="center">
        <Space size={4} align="center">
          <Tooltip title={role.name}>
            <Text strong style={{ fontSize: 14 }}>
              {role.name}
            </Text>
          </Tooltip>
          {isOwner ? (
            <Tag color="gold">{t('workspace.orgRoles.types.owner')}</Tag>
          ) : role.is_admin ? (
            <Tooltip title={t('workspace.orgRoles.matrix.adminHasFullAccess')}>
              <Tag color="blue">{t('workspace.orgRoles.types.admin')}</Tag>
            </Tooltip>
          ) : role.is_system ? (
            <Tag>{t('workspace.orgRoles.types.builtin')}</Tag>
          ) : null}
        </Space>
        {!isFixed ? (
          <Space size={0}>
            <Button
              type="text"
              size="small"
              icon={<Pencil size={14} />}
              onClick={() => onEdit(role)}
              aria-label={t('workspace.orgRoles.actions.edit')}
            />
            <Button
              type="text"
              size="small"
              danger
              icon={<Trash2 size={14} />}
              onClick={() => onDelete(role)}
              aria-label={t('workspace.orgRoles.actions.delete')}
            />
          </Space>
        ) : null}
      </Flex>

      {role.description ? (
        <Tooltip title={role.description}>
          <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
            {role.description}
          </Text>
        </Tooltip>
      ) : null}

      {!isFixed && onSelectAll && onDeselectAll ? (
        <Space size={4} style={{ marginTop: 2 }}>
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: 'auto' }}
            onClick={onSelectAll}
          >
            {t('workspace.orgRoles.actions.selectAll')}
          </Button>
          <Divider orientation="vertical" />
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: 'auto' }}
            onClick={onDeselectAll}
          >
            {t('workspace.orgRoles.actions.clear')}
          </Button>
        </Space>
      ) : null}
    </Flex>
  )
}
